/**
 * 权限代码映射配置
 *
 * 定义权限代码与 Arco Pro resource + action 格式之间的映射关系
 */

/**
 * 扁平化权限代码 → Arco Pro 格式映射
 *
 * 格式：{ permission_code: { resource, action } }
 */
export const PERMISSION_MAPPING: Record<
  string,
  { resource: string; action: string }
> = {
  // 商业项目管理模块 (10个权限)
  'business_project:view_all': { resource: 'business_project', action: 'read_all' },
  'business_project:view_department': {
    resource: 'business_project',
    action: 'read_department',
  },
  'business_project:view_own': { resource: 'business_project', action: 'read_own' },
  'business_project:create_all': { resource: 'business_project', action: 'create_all' },
  'business_project:create_department': { resource: 'business_project', action: 'create_department' },
  'business_project:create_own': { resource: 'business_project', action: 'create_own' },
  'business_project:change': { resource: 'business_project', action: 'change' },
  'business_project:design_confirm': {
    resource: 'business_project',
    action: 'design_confirm',
  },
  'business_project:archive': { resource: 'business_project', action: 'archive' },
  'business_project:delete': { resource: 'business_project', action: 'delete' },

  // 售前支持模块 (8个权限)
  'presale:view_all': { resource: 'presale', action: 'read_all' },
  'presale:view_department': { resource: 'presale', action: 'read_department' },
  'presale:view_own': { resource: 'presale', action: 'read_own' },
  'presale:create_all': { resource: 'presale', action: 'create_all' },
  'presale:create_department': { resource: 'presale', action: 'create_department' },
  'presale:create_own': { resource: 'presale', action: 'create_own' },
  'presale:follow': { resource: 'presale', action: 'follow' },
  'presale:delete': { resource: 'presale', action: 'delete' },

  // 专业贡献模块 (10个权限)
  'contribution:view_all': { resource: 'contribution', action: 'read_all' },
  'contribution:view_department': {
    resource: 'contribution',
    action: 'read_department',
  },
  'contribution:view_own': { resource: 'contribution', action: 'read_own' },
  'contribution:create_all': { resource: 'contribution', action: 'create_all' },
  'contribution:create_department': { resource: 'contribution', action: 'create_department' },
  'contribution:create_own': { resource: 'contribution', action: 'create_own' },
  'contribution:accept_all': { resource: 'contribution', action: 'accept_all' },
  'contribution:accept_department': { resource: 'contribution', action: 'accept_department' },
  'contribution:accept_own': { resource: 'contribution', action: 'accept_own' },
  'contribution:delete': { resource: 'contribution', action: 'delete' },

  // 业绩管理模块 (13个权限)
  'performance:view_all': { resource: 'performance', action: 'read_all' },
  'performance:view_department': {
    resource: 'performance',
    action: 'read_department',
  },
  'performance:view_own': { resource: 'performance', action: 'read_own' },
  'performance:create_all': { resource: 'performance', action: 'create_all' },
  'performance:create_department': { resource: 'performance', action: 'create_department' },
  'performance:create_own': { resource: 'performance', action: 'create_own' },
  'performance:register': { resource: 'performance', action: 'register' },
  'performance:accept': { resource: 'performance', action: 'accept' },
  'performance:settle': { resource: 'performance', action: 'settle' },
  'performance:payment': { resource: 'performance', action: 'payment' },
  'performance:split': { resource: 'performance', action: 'split' },
  'performance:view_settlement': { resource: 'performance', action: 'view_settlement' },
  'performance:view_payment': { resource: 'performance', action: 'view_payment' },

  // 成本管理模块 (12个权限 - 查看、录入、编辑)
  // 查看权限（区分数据范围）
  'cost:view_labor_all': { resource: 'cost', action: 'read_labor_all' },
  'cost:view_labor_department': { resource: 'cost', action: 'read_labor_department' },
  'cost:view_travel_all': { resource: 'cost', action: 'read_travel_all' },
  'cost:view_travel_department': { resource: 'cost', action: 'read_travel_department' },
  'cost:view_outsource_all': { resource: 'cost', action: 'read_outsource_all' },
  'cost:view_outsource_department': { resource: 'cost', action: 'read_outsource_department' },
  // 录入权限（只保留 _all，不区分数据范围）
  'cost:labor_entry_all': { resource: 'cost', action: 'labor_entry_all' },
  'cost:travel_entry_all': { resource: 'cost', action: 'travel_entry_all' },
  'cost:outsource_entry_all': { resource: 'cost', action: 'outsource_entry_all' },
  // 编辑权限（只保留 _all，不区分数据范围）
  'cost:edit_labor_all': { resource: 'cost', action: 'write_labor_all' },
  'cost:edit_travel_all': { resource: 'cost', action: 'write_travel_all' },
  'cost:edit_outsource_all': { resource: 'cost', action: 'write_outsource_all' },

  // 报表中心模块 (4个权限 - 查看)
  'report:view_signed_performance': { resource: 'report', action: 'view_signed_performance' },
  'report:view_completed_performance': {
    resource: 'report',
    action: 'view_completed_performance',
  },
  'report:view_acceptance_performance': {
    resource: 'report',
    action: 'view_acceptance_performance',
  },
  'report:view_hours_statistics': { resource: 'report', action: 'view_hours_statistics' },

  // 系统设置模块 (2个权限)
  'setting:view': { resource: 'setting', action: 'read' },
  'setting:edit_system': { resource: 'setting', action: 'write' },

  // 项目权限 - 项目管理（6个权限）
  'project:view': { resource: 'project', action: 'read' },
  'project:progress': { resource: 'project', action: 'update_progress' },
  'project:member_view': { resource: 'project_member', action: 'read' },
  'project:member_add': { resource: 'project_member', action: 'add' },
  'project:member_change_role': { resource: 'project_member', action: 'change_role' },
  'project:member_remove': { resource: 'project_member', action: 'remove' },

  // 项目权限 - 任务管理（10个权限）
  'task:view_all': { resource: 'task', action: 'read_all' },
  'task:view_own': { resource: 'task', action: 'read_own' },
  'task:create': { resource: 'task', action: 'create' },
  'task:edit_info_all': { resource: 'task', action: 'write_info_all' },
  'task:edit_info_own': { resource: 'task', action: 'write_info_own' },
  'task:edit_date': { resource: 'task', action: 'write_date' },
  'task:assign': { resource: 'task', action: 'assign' },
  'task:change_status_all': { resource: 'task', action: 'change_status_all' },
  'task:change_status_own': { resource: 'task', action: 'change_status_own' },
  'task:delete': { resource: 'task', action: 'delete' },

  // 项目权限 - 工时管理（8个权限）
  'hours:plan_add_own': { resource: 'hours', action: 'plan_add_own' },
  'hours:plan_edit_own': { resource: 'hours', action: 'plan_edit_own' },
  'hours:plan_add_all': { resource: 'hours', action: 'plan_add_all' },
  'hours:plan_edit_all': { resource: 'hours', action: 'plan_edit_all' },
  'hours:actual_add_own': { resource: 'hours', action: 'actual_add_own' },
  'hours:actual_edit_own': { resource: 'hours', action: 'actual_edit_own' },
  'hours:actual_add_all': { resource: 'hours', action: 'actual_add_all' },
  'hours:actual_edit_all': { resource: 'hours', action: 'actual_edit_all' },
};

/**
 * Arco Pro 格式 → 扁平化权限代码反向映射
 *
 * 格式：{ resource_action: permission_code }
 */
export const REVERSE_PERMISSION_MAPPING: Record<string, string> =
  Object.fromEntries(
    Object.entries(PERMISSION_MAPPING).map(([code, { resource, action }]) => [
      `${resource}_${action}`,
      code,
    ])
  );

/**
 * 将 action 标准化
 * 将各种 action 类型映射到标准的 read/write/delete
 */
export function normalizeAction(action: string): 'read' | 'write' | 'delete' {
  if (
    action.startsWith('read') ||
    action.startsWith('view') ||
    action === 'read_all' ||
    action === 'read_department' ||
    action === 'read_own'
  ) {
    return 'read';
  }
  if (
    action.startsWith('write') ||
    action.startsWith('edit') ||
    action.startsWith('create') ||
    action.startsWith('add') ||
    action.startsWith('update') ||
    action.startsWith('plan_') ||
    action.startsWith('actual_') ||
    action.startsWith('change_status') ||
    action === 'change' ||
    action === 'design_confirm' ||
    action === 'archive' ||
    action === 'follow' ||
    action === 'register' ||
    action === 'accept' ||
    action === 'settle' ||
    action === 'payment' ||
    action === 'split' ||
    action === 'assign' ||
    action === 'configure' ||
    action === 'configure_system' ||
    action === 'configure_project' ||
    action === 'manage' ||
    action === 'remove' ||
    action === 'labor_entry_all' ||
    action === 'travel_entry_all' ||
    action === 'outsource_entry_all'
  ) {
    return 'write';
  }
  return 'delete'; // delete 及其他操作
}
